// About.jsx
import React from "react";
import { motion } from "framer-motion";
import "./About.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fade = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.6 } },
};

const About = () => {
  const stats = [
    { number: "30+", label: "Projects shipped end-to-end" },
    { number: "92%", label: "Retention via long-term partnership" },
    { number: "4x", label: "Average ROI on growth sprints" },
    { number: "7–14d", label: "Typical turnaround for key pages" },
  ];

  const values = [
    {
      title: "Clarity over clutter",
      desc: "Simple messaging, clean UI, and measurable KPIs — so everyone knows what “good” looks like.",
    },
    {
      title: "Speed with quality",
      desc: "Rapid iterations, reusable systems, and strong QA so shipping fast doesn’t break trust.",
    },
    {
      title: "Performance as default",
      desc: "Core Web Vitals, conversion flow, and tracking are built in — not bolted on later.",
    },
    {
      title: "Partners, not vendors",
      desc: "We communicate transparently, document decisions, and stay accountable to outcomes.",
    },
  ];

  const capabilities = [
    "Brand & growth strategy (positioning, messaging, funnel)",
    "UI/UX for conversion (landing pages, product flows, CRO)",
    "Web development (React / WordPress / Shopify builds)",
    "SEO foundations + technical fixes (structure, speed, indexing)",
    "Analytics & tracking (GA4, events, pixels, dashboards)",
    "Performance marketing help (creative + landing alignment)",
  ];

  const process = [
    {
      step: "01",
      title: "Discovery",
      desc: "We audit the current site, analytics, competitors, and user journey. Then define KPIs, scope, and sprint plan.",
    },
    {
      step: "02",
      title: "Design System",
      desc: "We create a consistent visual language: typography, spacing, components, and layouts to keep everything coherent.",
    },
    {
      step: "03",
      title: "Build & Integrate",
      desc: "We develop fast, responsive pages with clean code, SEO structure, and the tools you already use.",
    },
    {
      step: "04",
      title: "Launch & Optimize",
      desc: "After launch, we iterate based on data: heatmaps, conversions, traffic sources, and drop-offs.",
    },
  ];

  return (
    <section className="ab">
      {/* HERO */}
      <div className="ab__hero">
        <div className="ab__heroWrap">
          <motion.div
            className="ab__heroLeft"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
          >
            <p className="ab__kicker">About Us</p>
            <h1 className="ab__title">
              We build <span>high-converting</span> digital experiences that turn
              attention into revenue.
            </h1>
            <p className="ab__lead">
              We’re a performance-first design & development partner for modern
              brands. Strategy, UI, engineering, and tracking — aligned in one
              execution system so growth becomes predictable.
            </p>

            <div className="ab__heroCtas">
              <a className="ab__btn ab__btn--primary" href="#ab-contact">
                Book a Growth Call
              </a>
              <a className="ab__btn ab__btn--ghost" href="#ab-work">
                See What We Do
              </a>
            </div>

            <div className="ab__heroBadges">
              <span className="ab__badge">Conversion-focused</span>
              <span className="ab__badge">SEO-ready</span>
              <span className="ab__badge">Fast & scalable</span>
              <span className="ab__badge">Transparent delivery</span>
            </div>
          </motion.div>

          <motion.div
            className="ab__heroRight"
            variants={fade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <div className="ab__heroCard">
              <div className="ab__heroCardTop">
                <p className="ab__miniLabel">What you get</p>
                <h3 className="ab__miniTitle">A growth-ready website system</h3>
              </div>

              <div className="ab__heroGrid">
                <div className="ab__pill">
                  <b>Design</b>
                  <span>UI that guides action</span>
                </div>
                <div className="ab__pill">
                  <b>Dev</b>
                  <span>Clean, scalable builds</span>
                </div>
                <div className="ab__pill">
                  <b>SEO</b>
                  <span>Technical + on-page</span>
                </div>
                <div className="ab__pill">
                  <b>Data</b>
                  <span>Tracking + insights</span>
                </div>
              </div>

              <div className="ab__heroProof">
                <div className="ab__proofItem">
                  <span className="ab__dot" />
                  <p>Weekly progress updates & documented decisions</p>
                </div>
                <div className="ab__proofItem">
                  <span className="ab__dot" />
                  <p>Mobile-first, speed-first, conversion-first approach</p>
                </div>
                <div className="ab__proofItem">
                  <span className="ab__dot" />
                  <p>Launch checklist: SEO, performance, analytics, QA</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="ab__heroGlow" aria-hidden="true" />
      </div>

      {/* STATS */}
      <div className="ab__section ab__section--tight">
        <div className="ab__wrap">
          <div className="ab__stats">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                className="ab__stat"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                <h3>{s.number}</h3>
                <p>{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* WHO WE ARE */}
      <div className="ab__section">
        <div className="ab__wrap ab__twoCol">
          <motion.div
            className="ab__card ab__card--soft"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow">Who we are</p>
            <h2 className="ab__h2">A lean team built for outcomes.</h2>
            <p className="ab__p">
              We partner with founders, startups, and growing businesses who want
              a website that actually performs — not just “looks good”.
            </p>
            <p className="ab__p">
              Our work blends strategy, UX, engineering, and analytics to create
              digital journeys that convert. Every section, button, and page has
              a reason to exist — and a metric to validate it.
            </p>

            <div className="ab__highlights">
              <div className="ab__hl">
                <b>Strategy</b>
                <span>Positioning, funnel, content plan</span>
              </div>
              <div className="ab__hl">
                <b>Execution</b>
                <span>Design system + build + QA</span>
              </div>
              <div className="ab__hl">
                <b>Optimization</b>
                <span>Data-led improvements & CRO</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="ab__card"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow" id="ab-work">
              What we do
            </p>
            <h2 className="ab__h2">Capabilities that compound growth.</h2>
            <ul className="ab__list">
              {capabilities.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>

            <div className="ab__note">
              <b>Best fit for:</b> Brands improving conversion, launching a new
              offer, rebuilding for SEO, or scaling paid campaigns with better
              landing pages.
            </div>
          </motion.div>
        </div>
      </div>

      {/* VALUES */}
      <div className="ab__section ab__section--muted">
        <div className="ab__wrap">
          <motion.div
            className="ab__head"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <p className="ab__eyebrow">How we work</p>
            <h2 className="ab__h2">Principles that keep delivery clean.</h2>
            <p className="ab__p ab__p--muted">
              A strong process removes chaos. These values guide how we make
              decisions, communicate, and ship work.
            </p>
          </motion.div>

          <div className="ab__grid4">
            {values.map((v, i) => (
              <motion.div
                key={i}
                className="ab__value"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
              >
                <h3 className="ab__h3">{v.title}</h3>
                <p className="ab__p ab__p--muted">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* PROCESS */}
      <div className="ab__section">
        <div className="ab__wrap">
          <motion.div
            className="ab__head"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <p className="ab__eyebrow">Delivery</p>
            <h2 className="ab__h2">A sprint-based system that ships.</h2>
            <p className="ab__p ab__p--muted">
              We move fast, but never random. Each phase ends with a clear output
              and next actions — so progress is visible.
            </p>
          </motion.div>

          <div className="ab__process">
            {process.map((p, i) => (
              <motion.div
                key={i}
                className="ab__step"
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
              >
                <div className="ab__stepNo">{p.step}</div>
                <div className="ab__stepBody">
                  <h3 className="ab__h3">{p.title}</h3>
                  <p className="ab__p ab__p--muted">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* MISSION / VISION */}
      <div className="ab__section ab__section--tight">
        <div className="ab__wrap ab__twoCol">
          <motion.div
            className="ab__card ab__card--border"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow">Mission</p>
            <h2 className="ab__h2">Make growth practical.</h2>
            <p className="ab__p ab__p--muted">
              Help brands scale with a clear strategy, strong execution, and
              measurable improvements — so the website becomes a revenue engine,
              not a brochure.
            </p>
          </motion.div>

          <motion.div
            className="ab__card ab__card--border"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="ab__eyebrow">Vision</p>
            <h2 className="ab__h2">Be the most trusted delivery partner.</h2>
            <p className="ab__p ab__p--muted">
              Become the go-to team for founders who value speed, quality, and
              honest communication — with systems that keep improving after
              launch.
            </p>
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="ab__cta" id="ab-contact">
        <div className="ab__wrap ab__ctaWrap">
          <motion.div
            className="ab__ctaCard"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
          >
            <h2 className="ab__ctaTitle">Ready to improve conversion?</h2>
            <p className="ab__ctaText">
              Share your website link and goal (leads, sales, bookings). We’ll
              reply with a quick audit + a clean sprint plan.
            </p>
            <div className="ab__ctaBtns">
              <a className="ab__btn ab__btn--primary" href="/contact">
                Contact Us
              </a>
              <a className="ab__btn ab__btn--ghost" href="mailto:hello@setuphelponline.com">
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;